import { useForm } from "@tanstack/react-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useBanCustomer } from "@/hooks/use-banned-customers";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onClose: () => void;
  customer: { email: string; phone: string; name: string };
  storeId?: string;
};

export function BanCustomerDialog({ open, onClose, customer, storeId }: Props) {
  const banMutation = useBanCustomer();
  const hasEmail = !!customer.email.trim();

  const form = useForm({
    defaultValues: {
      reason: "",
      notify: hasEmail,
    },
    onSubmit: async ({ value }) => {
      try {
        const entry = await banMutation.mutateAsync({
          email: customer.email,
          phone: customer.phone,
          name: customer.name,
          reason: value.reason,
          storeId,
          notify: hasEmail && value.notify,
        });
        if (hasEmail && value.notify && entry?.notifyError) {
          toast.warning(`Kupac banovan, ali email nije poslan: ${entry.notifyError}`);
        } else if (hasEmail && value.notify) {
          toast.success("Kupac banovan i obaviješten");
        } else {
          toast.success("Kupac banovan");
        }
        form.reset();
        onClose();
      } catch (e: any) {
        toast.error(e?.message || "Greška pri baniranju kupca");
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Banuj kupca</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="flex flex-col gap-3 text-xs">
            <div>
              <span className="text-muted-foreground">Ime:</span>{" "}
              <span className="font-medium">{customer.name}</span>
            </div>
            {customer.email && (
              <div>
                <span className="text-muted-foreground">Email:</span>{" "}
                <span className="font-medium">{customer.email}</span>
              </div>
            )}
            {customer.phone && (
              <div>
                <span className="text-muted-foreground">Telefon:</span>{" "}
                <span className="font-medium">{customer.phone}</span>
              </div>
            )}
            <div>
              <label className="text-muted-foreground block mb-1">Razlog (opcionalno)</label>
              <form.Field name="reason">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Razlog za ban..."
                  />
                )}
              </form.Field>
            </div>
            <form.Field name="notify">
              {(field) => (
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={hasEmail && field.state.value}
                    disabled={!hasEmail}
                    onCheckedChange={(v) => field.handleChange(v === true)}
                  />
                  <span>
                    <span className="font-medium">Pošalji obavještenje kupcu</span>
                    <span className="block text-muted-foreground">
                      {hasEmail
                        ? "Obavještenje o budućim narudžbama uz avansno plaćanje. Razlog bana se ne navodi."
                        : "Kupac nema email adresu."}
                    </span>
                  </span>
                </label>
              )}
            </form.Field>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>Odustani</Button>
            <Button type="submit" variant="destructive" disabled={banMutation.isPending}>
              {banMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
              Banuj
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
